import { Card, Skeleton, Typography } from "antd";
import type { EChartsOption } from "echarts";
import ReactECharts from "echarts-for-react";
import type { ProductCreatedTimeseriesPoint } from "../types";

const { Text } = Typography;

const VB_BLUE = "#195178";
const LINE_COLOR = "#faad14";

const fmtSv = (value: number) => new Intl.NumberFormat("sv-SE").format(value);

const MONTH_LABELS_SV = [
  "jan",
  "feb",
  "mar",
  "apr",
  "maj",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "dec",
] as const;

function periodToMonthLabel(period: string): string {
  const month = Number(period.split("-")[1]);
  if (!month || month < 1 || month > 12) {
    return period;
  }
  return MONTH_LABELS_SV[month - 1];
}

function buildOption(points: ProductCreatedTimeseriesPoint[]): EChartsOption {
  const categories = points.map((p) => periodToMonthLabel(p.period));
  const created = points.map((p) => p.created);
  const totals = points.map((p) => p.totalInCatalog);

  return {
    grid: { left: 8, right: 8, top: 30, bottom: 4, containLabel: true },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      valueFormatter: (value) => fmtSv(Number(value)),
    },
    legend: {
      data: ["Nya produkter", "Totalt i katalogen"],
      right: 0,
      top: 0,
      icon: "roundRect",
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: "rgba(0,0,0,0.65)" },
    },
    xAxis: {
      type: "category",
      data: categories,
      axisLine: { lineStyle: { color: "#f0f0f0" } },
      axisTick: { show: false },
      axisLabel: { color: "rgba(0,0,0,0.45)" },
    },
    yAxis: [
      {
        type: "value",
        name: "Nya",
        nameTextStyle: { color: "rgba(0,0,0,0.45)" },
        splitLine: { lineStyle: { color: "#f5f5f5" } },
        axisLabel: { color: "rgba(0,0,0,0.45)", formatter: fmtSv },
      },
      {
        type: "value",
        splitLine: { show: false },
        axisLabel: { color: "rgba(0,0,0,0.45)", formatter: fmtSv },
      },
    ],
    series: [
      {
        name: "Nya produkter",
        type: "bar",
        data: created,
        barMaxWidth: 26,
        itemStyle: {
          color: VB_BLUE,
          borderRadius: [4, 4, 0, 0],
        },
      },
      {
        name: "Totalt i katalogen",
        type: "line",
        yAxisIndex: 1,
        data: totals,
        smooth: true,
        showSymbol: false,
        lineStyle: { width: 2, color: LINE_COLOR },
        itemStyle: { color: LINE_COLOR },
      },
    ],
  };
}

export type NewProductsPerMonthChartProps = {
  points?: ProductCreatedTimeseriesPoint[];
  loading?: boolean;
};

export function NewProductsPerMonthChart({
  points = [],
  loading = false,
}: NewProductsPerMonthChartProps) {
  const option = buildOption(points);

  return (
    <Card
      bordered={false}
      className="criclebox"
      headStyle={{ padding: "1rem 1rem 0", borderBottom: "none" }}
      bodyStyle={{ padding: "1rem" }}
      title={
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start" }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, color: "rgba(0,0,0,0.85)" }}>
              Nya produkter per månad
            </div>
            <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
              Skapade produkter i katalogen, samt ackumulerat antal
            </Text>
          </div>
          <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
            Rullande 12 månader
          </Text>
        </div>
      }
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 8 }} title={false} />
      ) : (
        <div style={{ paddingTop: "1rem" }}>
          <ReactECharts
            option={option}
            style={{ height: 360, width: "100%" }}
            opts={{ renderer: "canvas" }}
          />
        </div>
      )}
    </Card>
  );
}
